import React from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { clsx } from '../../utils/helpers';

// ── Button ────────────────────────────────────────────────────
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
}
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary', size = 'md', loading, icon, children, className, disabled, ...props
}) => {
  const base = 'inline-flex items-center gap-2 font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    primary: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
    secondary: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 focus:ring-gray-300',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
    ghost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-300',
  };
  const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2.5 text-sm', lg: 'px-5 py-3 text-base' };
  return (
    <button className={clsx(base, variants[variant], sizes[size], className)} disabled={disabled || loading} {...props}>
      {loading ? <Spinner size="sm" /> : icon}
      {children}
    </button>
  );
};

// ── Spinner ───────────────────────────────────────────────────
export const Spinner: React.FC<{ size?: 'sm' | 'md' | 'lg'; className?: string }> = ({ size = 'md', className }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  return <div className={clsx('animate-spin rounded-full border-2 border-gray-200 border-t-green-600', sizes[size], className)} />;
};

export const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <Spinner size="lg" />
  </div>
);

// ── Modal ─────────────────────────────────────────────────────
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
}
export const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, size = 'md', footer }) => {
  if (!open) return null;
  const sizes = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-2xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={clsx('relative bg-white rounded-2xl shadow-2xl w-full animate-slide-up', sizes[size])}>
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <X size={18} />
            </button>
          </div>
        )}
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
};

// ── Badge ─────────────────────────────────────────────────────
type BadgeVariant = 'green' | 'red' | 'yellow' | 'blue' | 'gray' | 'orange';
export const Badge: React.FC<{ variant?: BadgeVariant; children: React.ReactNode; className?: string }> = ({
  variant = 'gray', children, className
}) => {
  const variants: Record<BadgeVariant, string> = {
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    gray: 'bg-gray-100 text-gray-700',
    orange: 'bg-orange-100 text-orange-800',
  };
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', variants[variant], className)}>
      {children}
    </span>
  );
};

// ── Alert ─────────────────────────────────────────────────────
type AlertType = 'info' | 'success' | 'warning' | 'error';
export const Alert: React.FC<{ type?: AlertType; title?: string; message: string; onClose?: () => void }> = ({
  type = 'info', title, message, onClose
}) => {
  const configs: Record<AlertType, { bg: string; icon: React.ReactNode }> = {
    info: { bg: 'bg-blue-50 border-blue-200 text-blue-800', icon: <Info size={16} className="text-blue-500 shrink-0" /> },
    success: { bg: 'bg-green-50 border-green-200 text-green-800', icon: <CheckCircle size={16} className="text-green-500 shrink-0" /> },
    warning: { bg: 'bg-yellow-50 border-yellow-200 text-yellow-800', icon: <AlertTriangle size={16} className="text-yellow-500 shrink-0" /> },
    error: { bg: 'bg-red-50 border-red-200 text-red-800', icon: <AlertCircle size={16} className="text-red-500 shrink-0" /> },
  };
  const c = configs[type];
  return (
    <div className={clsx('flex items-start gap-3 p-4 rounded-xl border text-sm', c.bg)}>
      {c.icon}
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold mb-0.5">{title}</p>}
        <p className="opacity-90">{message}</p>
      </div>
      {onClose && <button onClick={onClose} className="shrink-0 opacity-60 hover:opacity-100"><X size={14} /></button>}
    </div>
  );
};

// ── Empty State ───────────────────────────────────────────────
export const Empty: React.FC<{ icon?: React.ReactNode; title?: string; description?: string; action?: React.ReactNode }> = ({
  icon, title = 'No data found', description, action
}) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4 text-gray-400">
      {icon || <AlertCircle size={24} />}
    </div>
    <h3 className="text-sm font-semibold text-gray-700 mb-1">{title}</h3>
    {description && <p className="text-xs text-gray-500 max-w-xs mb-4">{description}</p>}
    {action}
  </div>
);

// ── Stat Card ─────────────────────────────────────────────────
export const StatCard: React.FC<{
  title: string; value: string | number; subtitle?: string;
  icon: React.ReactNode; trend?: { value: number; label?: string };
  color?: 'green' | 'blue' | 'yellow' | 'red' | 'purple';
}> = ({ title, value, subtitle, icon, trend, color = 'green' }) => {
  const colors = {
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-3">
        <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', colors[color])}>{icon}</div>
        {trend !== undefined && (
          <span className={clsx('text-xs font-medium', trend.value >= 0 ? 'text-green-600' : 'text-red-500')}>
            {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}%{trend.label ? ` ${trend.label}` : ''}
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 font-display">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
};

// ── Table ─────────────────────────────────────────────────────
interface Column<T> {
  key: string; header: string;
  render?: (row: T) => React.ReactNode;
  className?: string;
}
interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  keyField?: string;
  onRowClick?: (row: T) => void;
}
export function Table<T extends Record<string, any>>({ columns, data, loading, emptyMessage = 'No records found', keyField = 'id', onRowClick }: TableProps<T>) {
  if (loading) return <div className="p-8 flex justify-center"><Spinner /></div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            {columns.map(col => (
              <th key={col.key} className={clsx('table-header', col.className)}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr><td colSpan={columns.length}><Empty title={emptyMessage} /></td></tr>
          ) : (
            data.map((row, i) => (
              <tr
                key={row[keyField] || i}
                className={clsx('table-row', onRowClick && 'cursor-pointer')}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map(col => (
                  <td key={col.key} className={clsx('table-cell', col.className)}>
                    {col.render ? col.render(row) : row[col.key] ?? '-'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────
export const Pagination: React.FC<{
  page: number; pages: number; total: number; limit: number;
  onPageChange: (p: number) => void;
}> = ({ page, pages, total, limit, onPageChange }) => {
  if (pages <= 1) return null;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
      <span>Showing {start}–{end} of {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium">Prev</button>
        {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map(p => (
          <button key={p} onClick={() => onPageChange(p)} className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium', p === page ? 'bg-green-600 text-white' : 'border border-gray-200 hover:bg-gray-50')}>{p}</button>
        ))}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages} className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium">Next</button>
      </div>
    </div>
  );
};

// ── Confirm Dialog ────────────────────────────────────────────
export const ConfirmDialog: React.FC<{
  open: boolean; title: string; message: string;
  confirmLabel?: string; variant?: 'danger' | 'primary';
  onConfirm: () => void; onCancel: () => void; loading?: boolean;
}> = ({ open, title, message, confirmLabel = 'Confirm', variant = 'primary', onConfirm, onCancel, loading }) => (
  <Modal open={open} onClose={onCancel} title={title} size="sm"
    footer={<>
      <Button variant="secondary" onClick={onCancel} disabled={loading}>Cancel</Button>
      <Button variant={variant === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
    </>}
  >
    <p className="text-sm text-gray-600">{message}</p>
  </Modal>
);

// ── Form Field ────────────────────────────────────────────────
interface FieldProps {
  label: string; error?: string; required?: boolean; hint?: string; children: React.ReactNode;
}
export const FormField: React.FC<FieldProps> = ({ label, error, required, hint, children }) => (
  <div>
    <label className="form-label">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    {error && <p className="form-error">{error}</p>}
  </div>
);

// ── Section Card ──────────────────────────────────────────────
export const SectionCard: React.FC<{
  title?: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode; className?: string;
}> = ({ title, subtitle, action, children, className }) => (
  <div className={clsx('card', className)}>
    {(title || action) && (
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div>
          {title && <h3 className="text-sm font-semibold text-gray-800">{title}</h3>}
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

// ── Avatar ────────────────────────────────────────────────────
export const Avatar: React.FC<{ name: string; size?: 'sm' | 'md' | 'lg'; color?: string }> = ({
  name, size = 'md', color = '#16a34a'
}) => {
  const initials = name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  const sizes = { sm: 'w-7 h-7 text-xs', md: 'w-9 h-9 text-sm', lg: 'w-11 h-11 text-base' };
  return (
    <div className={clsx('rounded-full flex items-center justify-center font-semibold text-white shrink-0', sizes[size])}
      style={{ backgroundColor: color }}>
      {initials}
    </div>
  );
};
